import React, { createContext, useContext, useEffect, useState } from 'react';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import { clearToken, setToken } from '../../../infrastructure/http/client';
import { identifyUser, resetAnalytics } from '../../../infrastructure/browser/analytics';
import type { User } from '../../../domain/User';

interface AuthResult {
  ok: boolean;
  error?: string;
}

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<AuthResult>;
  register: (email: string, password: string, name: string) => Promise<AuthResult>;
  loginWithPasskey: () => Promise<AuthResult>;
  registerPasskey: (deviceName: string) => Promise<AuthResult>;
  updateName: (name: string) => Promise<AuthResult>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// El navegador cancela con "NotAllowedError" tanto si el usuario tocó
// "Cancelar" en el diálogo de huella como si se le acabó el tiempo — en
// ambos casos no hay nada roto, así que evitamos mostrar el mensaje técnico.
function webAuthnErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.name === 'NotAllowedError') {
    return 'Operación cancelada.';
  }
  return err instanceof Error ? err.message : fallback;
}

// Un solo refresh al arrancar, aunque el efecto corra dos veces (React
// StrictMode en desarrollo lo hace a propósito). Dos /auth/refresh en
// paralelo con la misma cookie hacen que el segundo llegue con un token ya
// rotado: el backend lo trata como token robado y revoca toda la sesión, y
// desde ahí todos los pedidos dan 401.
let restoreInFlight: Promise<{ token: string; user: User }> | null = null;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { auth } = useRepositories();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // No hay token guardado entre recargas (vive en memoria, a propósito):
    // la sesión se recupera acá pidiendo un access token nuevo con la cookie
    // httpOnly del refresh token. Si no hay cookie válida, esto falla y
    // queda deslogueado, como antes cuando no había token en localStorage.
    const restoreSession = async () => {
      try {
        restoreInFlight ??= auth.refreshAccessToken().finally(() => {
          restoreInFlight = null;
        });
        const { token, user: apiUser } = await restoreInFlight;
        setToken(token);
        setUser(apiUser);
      } catch {
        clearToken();
      } finally {
        setLoading(false);
      }
    };
    restoreSession();
  }, [auth]);

  // Cualquier forma de entrar (contraseña, huella, sesión recuperada) pasa
  // por acá.
  useEffect(() => {
    if (user) identifyUser(user);
  }, [user]);

  const login: AuthContextValue['login'] = async (email, password) => {
    try {
      const { token, user: apiUser } = await auth.login(email, password);
      setToken(token);
      setUser(apiUser);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'No se pudo iniciar sesión.' };
    }
  };

  const register: AuthContextValue['register'] = async (email, password, name) => {
    try {
      const { token, user: apiUser } = await auth.register(email, password, name);
      setToken(token);
      setUser(apiUser);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'No se pudo crear la cuenta.' };
    }
  };

  const loginWithPasskey: AuthContextValue['loginWithPasskey'] = async () => {
    try {
      const options = await auth.getWebAuthnLoginOptions();
      const response = await startAuthentication({ optionsJSON: options });
      const { token, user: apiUser } = await auth.verifyWebAuthnLogin(response);
      setToken(token);
      setUser(apiUser);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: webAuthnErrorMessage(err, 'No se pudo ingresar con huella.') };
    }
  };

  const registerPasskey: AuthContextValue['registerPasskey'] = async (deviceName) => {
    try {
      const options = await auth.getWebAuthnRegisterOptions();
      const response = await startRegistration({ optionsJSON: options });
      await auth.verifyWebAuthnRegistration(response, deviceName);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: webAuthnErrorMessage(err, 'No se pudo activar la huella en este dispositivo.') };
    }
  };

  const updateName: AuthContextValue['updateName'] = async (name) => {
    try {
      const { user: apiUser } = await auth.updateMe(name);
      setUser(apiUser);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'No se pudo actualizar el nombre.' };
    }
  };

  const logout = async () => {
    // No esperamos a que falle o no: aunque el request se pierda (sin red,
    // por ejemplo), igual queremos deslogueado en el cliente ya.
    auth.logout().catch(() => undefined);
    clearToken();
    resetAnalytics();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated: !!user, loading, login, register, loginWithPasskey, registerPasskey, updateName, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return ctx;
}
